// ***************************************************************************************
// The dictionary. Ported from server/src/dictionary.js, which reads the same three files
// the C game reads, in the same format, so there is one dictionary and not a copy that
// can disagree about whether a word exists.
//
//   english.txt   NUL-separated lowercase words, sorted by BYTE value.
//   rarity.bin    one byte per word, same order - the tier.
//   common.bin    one BIT per word, same order - eligibility for board construction.
//
// Words are compared as BYTES, not as JavaScript strings. The list is sorted by byte
// value and a locale-aware comparison will fail to find words that are there. Byte
// comparison is also what the C's strcmp does.
//
// An Int32Array of offsets rather than an array of strings costs 4 bytes a word instead
// of roughly 60, which is the difference between 1.4MB and 20MB of memory for 278,132
// words. That matters on a phone.
// ***************************************************************************************

import { TIER_ORDINARY } from './rules.ts';

export const COMMON_MAX_LENGTH = 8;

/** What board construction needs: the common list, bucketed by length. */
export interface CommonWords {
  countOfLength(length: number): number;
  wordOfLength(length: number, index: number): string | null;
}

export interface Dictionary extends CommonWords {
  readonly wordCount: number;
  readonly hasCommon: boolean;

  has(word: string): boolean;

  /**
   * Whether the word is on the COMMON list - the curated subset boards are built from.
   *
   * This is the closest thing the data has to "a word people actually know", and it is
   * already loaded. The generator uses it to choose between words that are equally
   * correct but not equally recognisable. Nothing in play consults it: every word in the
   * dictionary scores the same whether it is common or not.
   */
  isCommon(word: string): boolean;

  /** Where a word sits in the list, or -1. definitions.bin is keyed by this. */
  indexOf(word: string): number;
  tier(word: string): number;

  /**
   * Whether any word starts with this prefix.
   *
   * This is what makes the move search tractable, and it is why there is no trie. A trie
   * over 278k words costs megabytes to build on a phone; a binary search for the first
   * word >= prefix answers the same question in O(log n) against the bytes already
   * loaded, with no extra structure at all. The brief asks for "a trie or equivalent" -
   * this is the equivalent.
   */
  hasPrefix(prefix: string): boolean;
}

function buildIndex(bytes: Uint8Array): { offsets: Int32Array; count: number } {
  let count = 0;

  for (let i = 0; i < bytes.length; i++) {
    if (bytes[i] === 0) count++;
  }

  // A file that does not end in a terminator still has a last word.
  if (bytes.length > 0 && bytes[bytes.length - 1] !== 0) count++;

  const offsets = new Int32Array(count + 1);

  let at = 0;
  let index = 0;

  offsets[0] = 0;

  for (let i = 0; i < bytes.length; i++) {
    if (bytes[i] !== 0) continue;

    index++;
    at = i + 1;

    if (index <= count) offsets[index] = at;
  }

  if (index < count) offsets[count] = bytes.length;

  offsets[count] = Math.max(offsets[count] ?? 0, bytes.length);

  return { offsets, count };
}

/** strcmp against the word at `index`: negative if the stored word sorts first. */
function compareAt(
  bytes: Uint8Array,
  offsets: Int32Array,
  index: number,
  query: Uint8Array,
): number {
  let at = offsets[index]!;

  for (let i = 0; i < query.length; i++) {
    const stored = at < bytes.length ? bytes[at]! : 0;

    if (stored === 0) return -1; // stored word is shorter, so it sorts first

    const diff = stored - query[i]!;

    if (diff !== 0) return diff < 0 ? -1 : 1;

    at++;
  }

  const trailing = at < bytes.length ? bytes[at]! : 0;

  return trailing === 0 ? 0 : 1;
}

/** Like compareAt, but the query only has to be a PREFIX of the stored word to match. */
function comparePrefixAt(
  bytes: Uint8Array,
  offsets: Int32Array,
  index: number,
  query: Uint8Array,
): number {
  let at = offsets[index]!;

  for (let i = 0; i < query.length; i++) {
    const stored = at < bytes.length ? bytes[at]! : 0;

    if (stored === 0) return -1;

    const diff = stored - query[i]!;

    if (diff !== 0) return diff < 0 ? -1 : 1;

    at++;
  }

  return 0;
}

export function createDictionary(
  wordBytes: Uint8Array,
  rarityBytes: Uint8Array | null,
  commonBits: Uint8Array | null,
): Dictionary {
  const { offsets, count } = buildIndex(wordBytes);

  const encoder = new TextEncoder();
  const decoder = new TextDecoder();

  function find(word: string): number {
    if (typeof word !== 'string' || word.length === 0) return -1;

    const query = encoder.encode(word);

    let lo = 0;
    let hi = count - 1;

    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      const cmp = compareAt(wordBytes, offsets, mid, query);

      if (cmp === 0) return mid;

      if (cmp < 0) lo = mid + 1;
      else hi = mid - 1;
    }

    return -1;
  }

  function lengthAt(index: number): number {
    const start = offsets[index]!;
    const stop = offsets[index + 1] ?? wordBytes.length;

    let at = start;

    while (at < stop && at < wordBytes.length && wordBytes[at] !== 0) at++;

    return at - start;
  }

  function textAt(index: number): string {
    const start = offsets[index]!;

    return decoder.decode(wordBytes.subarray(start, start + lengthAt(index)));
  }

  // ***************************************************************************************
  // The common words, bucketed by length.
  //
  // The ORDER is the point, not just the membership: board construction picks its words
  // with rng.bounded(count) as an index into a bucket, so a different order rebuilds a
  // different board. Dictionary_LoadCommon walks the list from index 0 and appends; so
  // does this.
  //
  // Built lazily, because only board construction needs it and the client normally
  // receives its board from the API already built.
  // ***************************************************************************************
  let commonByLength: number[][] | null = null;

  function isCommonIndex(index: number): boolean {
    if (!commonBits) return false;
    if (index < 0 || index >= count) return false;

    const at = index >> 3;

    if (at >= commonBits.length) return false;

    return (commonBits[at]! & (1 << (index & 7))) !== 0;
  }

  function common(): number[][] {
    if (commonByLength) return commonByLength;

    const buckets: number[][] = [];

    for (let length = 0; length <= COMMON_MAX_LENGTH; length++) buckets.push([]);

    if (commonBits) {
      for (let i = 0; i < count; i++) {
        if (!isCommonIndex(i)) continue;

        const length = lengthAt(i);

        if (length > COMMON_MAX_LENGTH) continue;

        buckets[length]!.push(i);
      }
    }

    commonByLength = buckets;

    return buckets;
  }

  return {
    wordCount: count,
    hasCommon: !!commonBits,

    has(word: string): boolean {
      return find(word) >= 0;
    },

    isCommon(word: string): boolean {
      if (!commonBits) return false;

      return isCommonIndex(find(word));
    },

    indexOf(word: string): number {
      return find(word);
    },

    tier(word: string): number {
      if (!rarityBytes) return TIER_ORDINARY;

      const at = find(word);

      // Not an error: a dictionary served without its rarity file scores everything as
      // ordinary rather than refusing to run.
      if (at < 0 || at >= rarityBytes.length) return TIER_ORDINARY;

      return rarityBytes[at]!;
    },

    hasPrefix(prefix: string): boolean {
      if (prefix.length === 0) return count > 0;

      const query = encoder.encode(prefix);

      let lo = 0;
      let hi = count - 1;

      while (lo <= hi) {
        const mid = (lo + hi) >> 1;
        const cmp = comparePrefixAt(wordBytes, offsets, mid, query);

        if (cmp === 0) return true;

        if (cmp < 0) lo = mid + 1;
        else hi = mid - 1;
      }

      return false;
    },

    countOfLength(length: number): number {
      if (length < 0 || length > COMMON_MAX_LENGTH) return 0;

      return common()[length]!.length;
    },

    wordOfLength(length: number, index: number): string | null {
      if (length < 0 || length > COMMON_MAX_LENGTH) return null;

      const bucket = common()[length]!;

      if (index < 0 || index >= bucket.length) return null;

      return textAt(bucket[index]!);
    },
  };
}

// ***************************************************************************************
// Loading.
//
// `version` is part of the path so the files can be cached immutably and a dictionary
// change cannot be served to a client holding a board built from the old one.
// ***************************************************************************************
export interface DictionarySource {
  readonly base: string;
  readonly version: string;
  /** Board construction needs common.bin; play does not. */
  readonly withCommon: boolean;
}

export async function loadDictionary(source: DictionarySource): Promise<Dictionary> {
  async function bytes(name: string): Promise<Uint8Array | null> {
    const response = await fetch(`${source.base}/${name}?v=${source.version}`);

    if (!response.ok) return null;

    return new Uint8Array(await response.arrayBuffer());
  }

  const words = await bytes('english.txt');

  if (!words) throw new Error('dictionary: english.txt could not be loaded');

  const [rarity, commonBits] = await Promise.all([
    bytes('rarity.bin'),
    source.withCommon ? bytes('common.bin') : Promise.resolve(null),
  ]);

  return createDictionary(words, rarity, commonBits);
}
